import React, { JSX } from "react";
import "./ViewEntryModal.css";

type ViewEntryModalProps = {
  open: boolean;
  onClose: () => void;
  entry: {
    id: string;
    title: string | null;
    content: string;
    image_url: string | null;
    created_at: string;
    reactions_count: number;
    author?: {
      display_name: string | null;
      avatar_url: string | null;
    };
  } | null;
  hasReacted: boolean;
  onToggleReaction: (entryId: string) => void;
};

export default function ViewEntryModal({ open, onClose, entry, hasReacted, onToggleReaction }: ViewEntryModalProps): JSX.Element | null {
  if (!open || !entry) return null;

  // Format relative time (e.g., "2d ago", "5h ago")
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const authorName = entry.author?.display_name || "Anonymous";
  const authorAvatar = entry.author?.avatar_url;

  return (
    <div className="view-entry-modal">
      <div className="overlay" onClick={onClose}>
        {/* Content card */}
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Header with avatar, name, time, and close button */}
          <div className="modal-header">
            <div className="author-info">
              {authorAvatar ? (
                <img src={authorAvatar} alt={authorName} className="author-avatar" />
              ) : (
                <div className="author-avatar-placeholder">
                  {authorName.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="author-details">
                <span className="author-name">{authorName}</span>
                <span className="entry-time">{formatRelativeTime(entry.created_at)}</span>
              </div>
            </div>
            <button className="modal-close-button" onClick={onClose}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Image area (if entry has an image) */}
          {entry.image_url && (
            <div className="entry-image-container">
              <img src={entry.image_url} alt="" className="entry-full-image" />
            </div>
          )}

          {/* Entry content */}
          <div className="entry-content">
            {entry.title && (
              <h3 className="entry-title">{entry.title}</h3>
            )}
            <p className="entry-text">{entry.content}</p>
          </div>

          {/* Action bar with heart */}
          <div className="modal-action-bar">
            <button
              className={`modal-heart-button ${hasReacted ? 'active' : ''}`}
              onClick={() => onToggleReaction(entry.id)}
            >
              <svg
                className={`modal-heart-icon ${hasReacted ? 'active' : ''}`}
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill={hasReacted ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </button>
            {entry.reactions_count > 0 && (
              <span className={`modal-heart-count ${hasReacted ? 'active' : ''}`}>
                {entry.reactions_count}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
